<script>
import ShiftCell from './ShiftCell.vue';
import ShiftWeeklyCell from './components/ShiftWeeklyCell.vue';
import { dateFromSecondsTimestamp, intToJapaneseWeekdayKanji, millisecondsFromSeconds } from '../js/common.js'
import { isMonthlyView, isWeeklyView, isDailyView } from '../js/shift';
import { fromOplusTime, yearMonthDateString } from '../common/common';
import { MeStore } from '../store/v1/meStore';
import timeZones from '../common/timeZones';
import TimeIndicator from './components/TimeIndicator.vue';

export default {
  props: {
    dates: Object, // All DayData object
    branchSettings: Object,
    meStore: MeStore,
  },
  data() {
    return {
      days: Object.values(intToJapaneseWeekdayKanji),
      selectedCellDate: 0,
      selectedRoleID: "",
      cellHeight: 60,
    }
  },
  methods: {
    selectedCellHandler(date) {
      return this.selectedCellDate = new Date(date).valueOf();
    },
    closePopup(event) {
      event.stopPropagation();
      return this.selectedCellDate = 0;
    },
    holidayClass(index) {
      return {
        "th-saturday": index === 6,
        "th-holiday": index === 0,
      }
    },
  },
  computed: {
    datesArray() {
      const datesArray = [];
      const startDate = new Date(millisecondsFromSeconds(this.dates[0].TimeStamp));
      if (this.isCalendarDailyView) {
        const oplusDate = dateFromSecondsTimestamp(this.dates[0].TimeStamp);
        const realDate = fromOplusTime(oplusDate);
        datesArray.push({
          dateStr: yearMonthDateString(realDate, 'Asia/Tokyo'),
          timeStamp: millisecondsFromSeconds(this.dates[0].TimeStamp),
          isHoliday: this.dates[0].IsHoliday
        });

        return datesArray;
      }
      if (!this.isCalendarWeeklyView) {
        // Padding before the month starts
        for (let i = 0; i < startDate.getUTCDay(); i++) {
          datesArray.push("");
        }
      }

      // Add each day of the month to the array
      for (let i = 0; i < this.dates.length; i++) {
        const oplusDate = dateFromSecondsTimestamp(this.dates[i].TimeStamp);
        const realDate = fromOplusTime(oplusDate);
        datesArray.push({
          dateStr: yearMonthDateString(realDate, timeZones.ASIA_TOKYO),
          timeStamp: millisecondsFromSeconds(this.dates[i].TimeStamp),
          isHoliday: this.dates[i].IsHoliday
        });
      }

      // Padding after the month ends
      while (datesArray.length % 7 !== 0) {
        datesArray.push("");
      }
      return datesArray;
    },
    isCalendarMonthlyView() {
      return isMonthlyView();
    },
    isCalendarWeeklyView() {
      return isWeeklyView();
    },
    isCalendarDailyView() {
      return isDailyView();
    },
    timeViewArray() {
      const timeRangeArr = [];
      const start = this.branchSettings.summaryDayStart;
      const end = this.branchSettings.summaryDayEnd;  // end may be over 24 hours depending on working hours

      for (let i = start; i < end; i++) {
        timeRangeArr.push(i % 24);
      }

      return [...new Set(timeRangeArr)];
    },
    computedGridStyle() {
      return {
        "gridTemplateRows": `${this.cellHeight + 30}px repeat(${this.timeViewArray.length}, ${this.cellHeight}px)`
      }
    },
    rowClass() {
      return {
        'week': this.isCalendarWeeklyView,
        'daily': this.isCalendarDailyView,
      }
    }
  },
  components: {
    ShiftCell,
    ShiftWeeklyCell,
    TimeIndicator,
  },
  inject: [
    'calendarReactiveObject'
  ],
}
</script>
<template>
  <!-- Monthly Calendar -->
  <div v-if="calendarReactiveObject.isCalendarView && isCalendarMonthlyView" id="shift-calendar" class="calendar-container">
    <!-- TODO: Separate the Monthly calendar into a component, both Monthly and Weekly components should be under one parent component -->
    <!-- Top most row, weekday name row -->
    <div class="calendar-rows weekday-name-row">
      <div v-for="day, index in days" class="day-name" :key="index">
        <span :class="holidayClass(index)">{{ day }}</span>
      </div>
    </div>
    <!-- Main Calendar Body -->
    <div class="calendar-body calendar-rows month">
      <div v-for="date, index in datesArray" :key="index">
        <div v-if="!date" class="day-cell-wrapper empty-cell"></div>
        <ShiftCell v-else
          :cell-props="calendarReactiveObject.cellsMap[date.dateStr]"
          :cell-index="index"
          :is-selected="selectedCellDate === date.timeStamp"
          :is-holiday="date.isHoliday"
          :branch-settings="branchSettings"
          :date="date.dateStr"
          :me-store="meStore"
          @emit-selected-cell="selectedCellHandler"
          @emit-close-popup="closePopup"
        />
      </div>
    </div>
  </div>

  <!-- Weekly Calendar -->
  <div v-if="calendarReactiveObject.isCalendarView && !isCalendarMonthlyView" id="shift-weekly-calendar" class="calendar-weekly-content">
    <div class="calendar-rows" :class="rowClass">
      <TimeIndicator
        :cell-height="cellHeight"
        :time-view="timeViewArray"
      />
      <!-- Time range column -->
      <div class="weekly-day-cell-wrapper time-cell-sticky">
        <div class="time-cell-wrapper" :style="computedGridStyle">
          <div class="weekly-calendar-corner-cell"></div>
          <div v-for="time in timeViewArray" class="time-cell time-text-wrapper">
            <span class="time-text">{{ time + ":00"  }}</span>
          </div>
        </div>
      </div>

      <div v-for="date, index in datesArray" :key="index" class="day-cell-wrapper weekly-day-cell-wrapper">
        <ShiftWeeklyCell
          v-if="calendarReactiveObject.cellsMap[date.dateStr]"
          :cell-props="calendarReactiveObject.cellsMap[date.dateStr]"
          :is-cell-selected="selectedCellDate === date.timeStamp"
          :is-holiday="date.isHoliday"
          :time-view="timeViewArray"
          :branch-settings="branchSettings"
          :date="date.dateStr"
          :me-store="meStore"
          :cell-height="cellHeight"
          @emit-selected-cell="selectedCellHandler"
          @emit-close-popup="closePopup"
        />
      </div>

    </div>
  </div>
</template>

<style>
  .calendar-container {
    height: 100%;
    min-width: 300px;
    display: flex;
    flex-direction: column;
  }

  .calendar-weekly-content {
    height: 100%;
    border: .05rem solid #e0e0e0;
    border-radius: 5px;
    overflow: auto;
    overscroll-behavior-x: none;
  }

  .calendar-body {
    border: .05rem solid #e0e0e0;
    border-radius: 5px;
    cursor: pointer;
  }

  .calendar-rows {
    display: grid;
    grid-template-columns: repeat(7, minmax(40px,1fr));
  }

  .month, .week {
    flex-grow: 1;
    height: 100%;
  }

  .calendar-rows.week {
    grid-template-columns: 58px repeat(7, minmax(90px,1fr));
    position: relative;
  }

  .calendar-rows.daily {
    grid-template-columns: 58px 1fr;
    position: relative;
  }

  .weekday-name-row {
    font-weight: bolder;
    text-align: center;
    height: 24px;
  }

  .empty-cell {
    background-color: #a8a8a810;
    height: 100%;
    width: 100%;
  }

  /* Shift cell (monthly and weekly) styles */

  .day-cell-wrapper {
    border: .05rem solid #e0e0e070;
    text-align: center;
    height: 100%;
    cursor: pointer;
  }

  .weekly-day-cell-wrapper {
    border-right: .05rem solid transparent;
  }

  .day-cell-wrapper:not(.empty-cell):hover,
  .weekly-cell-header:not(.daily-header):hover {
    border: .05rem solid #52a3d58b;
    border-radius: 7px;
    background-color: #eef6fb;
    transition: .2s ease;
  }

  .day-cell-wrapper:not(.empty-cell):has(.weekly-cell-header:not(.daily-header):hover) {
    border: unset;
    border-left: .05rem solid #e0e0e070;
    border-radius: 0;
    background-color: #fff;
  }

  .day-cell {
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: flex-start;
    gap: 6px;
    min-height: 41px;
    height: 99%;
    width: 100%;
    overflow: hidden;
    position: relative;
    padding: 5px;
  }

  .day-cell-content {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .day-cell-content-inner {
    position: absolute;
    width: 100%;
    top: 0;
    left: 50%;
    gap: 10px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    height: -webkit-fill-available;
    height: fill-available;
    height: -moz-available;
  }

  .today-cell {
    line-height: 0;
    background-color: #ffce41;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .cell-details-container {
    position: fixed;
    display: block;
    overflow: hidden;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    z-index: 100000;
  }

  .cell-details-overlay {
    background: rgba(247,248,249,.6);
    cursor: default;
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  .cell-details-card {
    text-align: center;
    width: 270px;
    height: auto;
    max-height: 450px;
    min-height: 275px;
    background-color: #fff;
    z-index: 100001;
    box-shadow: 0px 1px 4px 1px #8e8e8e91;
    padding: 10px 10px;
    border-radius: 5px;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .card-date-text {
    padding-bottom: 5px;
    border-bottom: .05rem solid #e0e0e0;
  }

  .add-slot-button-wrapper {
    margin-top: auto;
    padding-top: 10px;
    border-top: .05rem solid #e0e0e0;
  }

  .empty-slot-text {
    font-size: .6rem;
  }

  .cell-add-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 4px;
  }

  .calendar-slots {
    gap: 8px;
  }

  .calendar-slots-container {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 20px;
  }

  .calendar-slots-cell {
    pointer-events: none;
    width: 100%;
  }

  .time-range-container {
    margin: .05rem 0;
    z-index: 3;
    position: sticky;
    left: 0;
    background: #fff;
  }

  .time-cell-sticky {
    position: sticky;
    left: 0;
    z-index: 5;
    background-color: #fff;
  }

  .time-cell-wrapper {
    position: relative;
    display: grid;
    margin-top: 0.1rem;
  }

  .calendar-weekly-content .time-cell {
    border-top: .05rem solid #e0e0e0;
    width: 100%;
  }

  .time-range-wrapper {
    width: 100%;
    height: 100%;
    display: grid;
  }

  .time-text-wrapper {
    position: relative;
  }

  .time-text{
    position: absolute;
    top: -25%;
    height: 50%;
    width: 58px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    background-color: #fff;
  }

  .weekly-calendar-corner-cell {
    position: sticky;
    left: 0;
    top: 0;
    background: linear-gradient(#fff 50%, transparent 50%);
    z-index: 4;
  }

  /* ANIMATION CLASSES */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  .left-fade-enter-active,
  .right-fade-enter-active,
  .top-fade-enter-active {
    transition: all 0.2s ease-in-out;
  }
  .left-fade-leave-active,
  .right-fade-leave-active,
  .top-fade-leave-active {
    transition: all 0.2s linear;
  }
  .left-fade-enter-from,
  .left-fade-leave-to {
    transform: translateX(-20px);
    opacity: 0;
  }
  .right-fade-enter-from,
  .right-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
  }

  .top-fade-enter-from,
  .top-fade-leave-to {
    transform: translateY(-20px);
    opacity: 0;
  }

  .slot-memo {
    margin: 5px 10px;
    font-size: 12px;
    text-align: left;
    white-space: pre-wrap;
  }

  .slot-memo-container {
    display: flex;
    overflow: hidden;
    border: 1px solid;
    border-radius: 5px;
    width: 95%;
  }

  /* Shift cell style END */
  @media (max-width: 576px) {
    .cell-details-card.monthly-modal {
      width: 160px;
      height: 275px;
    }
    .day-cell {
      font-size: .7rem;
      gap: 8px;
    }
    .today-cell {
      height: 25px;
      width: 25px;
    }
    .cell-add-button {
      top: auto;
      bottom: 0;
      left: 0;
    }
    .date-label-wrapper {
      display: flex;
      justify-content: center;
      line-height: normal;
      width: 100%;
    }

    .month {
      height: 50px;
    }
  }

  @media (max-width: 430px) {
    .calendar-rows.week {
      width: fit-content;
    }
  }

  @media (max-width: 300px)  {
    .card-date-text {
      font-size: .6rem;
    }
    .cell-details-card.monthly-modal {
      width: 130px;
    }
  }


  @media (max-width: 769px) {
    .month {
      height: 70px;
    }
  }
</style>
